import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from 'firebase/auth';
import { auth } from '../firebase';
import './AuthAction.css';

type Mode = 'verifyEmail' | 'resetPassword' | null;
type Status = 'loading' | 'success' | 'error' | 'form';

const translations = {
  fr: {
    loading: 'Vérification en cours…',
    emailVerified: 'Email confirmé !',
    emailVerifiedBody: "Ton adresse email a bien été vérifiée. Tu peux retourner sur l'app FlowXP.",
    emailVerifiedHint: "L'app se mettra à jour automatiquement.",
    passwordChanged: 'Mot de passe modifié !',
    passwordChangedBody: "Ton nouveau mot de passe a bien été enregistré. Tu peux te connecter sur l'app FlowXP.",
    errorTitle: 'Une erreur est survenue',
    invalidLink: 'Lien invalide ou expiré.',
    expiredVerification: "Ce lien est invalide ou a expiré. Retourne sur l'app pour en recevoir un nouveau.",
    expiredReset: "Ce lien est invalide ou a expiré. Refais une demande de réinitialisation depuis l'app.",
    invalidMode: 'Lien invalide.',
    newPassword: 'Nouveau mot de passe',
    newPasswordBody: "Choisis un nouveau mot de passe pour ton compte FlowXP.",
    newPasswordPlaceholder: 'Nouveau mot de passe',
    confirmPasswordPlaceholder: 'Confirmer le mot de passe',
    saveButton: 'Enregistrer le mot de passe',
    savingButton: 'Enregistrement…',
    passwordTooShort: 'Le mot de passe doit contenir au moins 6 caractères.',
    passwordMismatch: 'Les mots de passe ne correspondent pas.',
    resetError: "Erreur lors de la réinitialisation. Réessaie depuis l'app.",
  },
  en: {
    loading: 'Verifying…',
    emailVerified: 'Email confirmed!',
    emailVerifiedBody: 'Your email address has been verified. You can go back to the FlowXP app.',
    emailVerifiedHint: 'The app will update automatically.',
    passwordChanged: 'Password updated!',
    passwordChangedBody: 'Your new password has been saved. You can log in to the FlowXP app.',
    errorTitle: 'An error occurred',
    invalidLink: 'Invalid or expired link.',
    expiredVerification: 'This link is invalid or has expired. Go back to the app to receive a new one.',
    expiredReset: 'This link is invalid or has expired. Request a new reset from the app.',
    invalidMode: 'Invalid link.',
    newPassword: 'New password',
    newPasswordBody: 'Choose a new password for your FlowXP account.',
    newPasswordPlaceholder: 'New password',
    confirmPasswordPlaceholder: 'Confirm password',
    saveButton: 'Save password',
    savingButton: 'Saving…',
    passwordTooShort: 'Password must be at least 6 characters.',
    passwordMismatch: 'Passwords do not match.',
    resetError: 'Reset failed. Please try again from the app.',
  },
};

const AuthAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') as Mode;
  const oobCode = searchParams.get('oobCode') || '';
  const urlLang = searchParams.get('lang');

  const lang = (urlLang === 'en' || (!urlLang && !navigator.language.startsWith('fr'))) ? 'en' : 'fr';
  const t = translations[lang];

  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setError(t.invalidLink);
      setStatus('error');
      return;
    }
    if (mode === 'verifyEmail') {
      applyActionCode(auth, oobCode)
        .then(() => setStatus('success'))
        .catch(() => {
          setError(t.expiredVerification);
          setStatus('error');
        });
    } else if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then(() => setStatus('form'))
        .catch(() => {
          setError(t.expiredReset);
          setStatus('error');
        });
    } else {
      setError(t.invalidMode);
      setStatus('error');
    }
  }, [mode, oobCode, t]);

  const handlePasswordReset = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password.length < 6) {
      setError(t.passwordTooShort);
      return;
    }
    if (password !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
    } catch {
      setError(t.resetError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-action-page">
      <div className="auth-action-card">
        <img src="/new-icon-fxp.png" alt="FlowXP" className="auth-action-logo" />

        {status === 'loading' && (
          <div className="auth-action-loading">
            <div className="auth-action-spinner" />
            <p>{t.loading}</p>
          </div>
        )}

        {status === 'success' && mode === 'verifyEmail' && (
          <div className="auth-action-success">
            <div className="auth-action-icon">✓</div>
            <h1>{t.emailVerified}</h1>
            <p>{t.emailVerifiedBody}</p>
            <p className="auth-action-hint">{t.emailVerifiedHint}</p>
          </div>
        )}

        {status === 'success' && mode === 'resetPassword' && (
          <div className="auth-action-success">
            <div className="auth-action-icon">✓</div>
            <h1>{t.passwordChanged}</h1>
            <p>{t.passwordChangedBody}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="auth-action-error">
            <div className="auth-action-icon auth-action-icon--error">✕</div>
            <h1>{t.errorTitle}</h1>
            <p>{error}</p>
          </div>
        )}

        {status === 'form' && (
          <div className="auth-action-form-container">
            <h1>{t.newPassword}</h1>
            <p>{t.newPasswordBody}</p>
            <form onSubmit={handlePasswordReset} className="auth-action-form">
              <input
                type="password"
                placeholder={t.newPasswordPlaceholder}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="auth-action-input"
                minLength={6}
                required
              />
              <input
                type="password"
                placeholder={t.confirmPasswordPlaceholder}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="auth-action-input"
                required
              />
              {error && <p className="auth-action-field-error">{error}</p>}
              <button type="submit" className="auth-action-button" disabled={submitting}>
                {submitting ? t.savingButton : t.saveButton}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthAction;
